import type { ReactNode } from "react";
import { Link } from "react-router-dom";

type ButtonVariant = "primary" | "accent" | "outline" | "ghost" | "light";
type ButtonSize = "sm" | "md" | "lg";

interface ButtonProps {
  children: ReactNode;
  to?: string;
  href?: string;
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: string;
  className?: string;
  type?: "button" | "submit";
  onClick?: () => void;
  ariaLabel?: string;
  fullWidth?: boolean;
}

const variantClasses: Record<ButtonVariant, string> = {
  // Primary — Royal Blue → Violet gradient
  primary:
    "bg-gradient-to-br from-primary-500 to-accent-500 text-background-50 shadow-sm shadow-primary-500/25 hover:from-primary-600 hover:to-accent-600 hover:shadow-primary-600/35",
  // Medical CTA — Teal → Cyan gradient
  accent:
    "bg-gradient-to-br from-secondary-500 to-secondary-400 text-background-50 shadow-sm shadow-secondary-500/25 hover:from-secondary-600 hover:to-secondary-500",
  // Secondary — blue outline on a clean base
  outline:
    "border border-primary-300 bg-background-50 text-primary-700 hover:border-primary-400 hover:bg-primary-50",
  ghost: "text-primary-700 hover:bg-primary-50",
  // For use on dark/gradient backgrounds
  light: "bg-background-50 text-primary-700 hover:bg-secondary-50",
};

const sizeClasses: Record<ButtonSize, string> = {
  sm: "px-4 py-2 text-sm",
  md: "px-5 py-2.5 text-sm",
  lg: "px-6 py-3 text-base",
};

export default function Button({
  children,
  to,
  href,
  variant = "primary",
  size = "md",
  icon,
  className = "",
  type = "button",
  onClick,
  ariaLabel,
  fullWidth = false,
}: ButtonProps) {
  const classes = [
    "inline-flex items-center justify-center gap-2 rounded-md font-label font-semibold whitespace-nowrap transition-all duration-200 cursor-pointer",
    variantClasses[variant],
    sizeClasses[size],
    fullWidth ? "w-full" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const inner = (
    <>
      {icon ? <i className={`${icon} text-base`} aria-hidden="true"></i> : null}
      <span>{children}</span>
    </>
  );

  if (to) {
    return (
      <Link to={to} className={classes} aria-label={ariaLabel}>
        {inner}
      </Link>
    );
  }

  if (href) {
    const isExternal = href.startsWith("http");
    return (
      <a
        href={href}
        className={classes}
        aria-label={ariaLabel}
        target={isExternal ? "_blank" : undefined}
        rel={isExternal ? "noopener noreferrer" : undefined}
      >
        {inner}
      </a>
    );
  }

  return (
    <button type={type} onClick={onClick} className={classes} aria-label={ariaLabel}>
      {inner}
    </button>
  );
}